import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Vehicle, Sale, PAYMENT_METHODS, SaleInstallment } from '../../types';
import { CONTRATO_VENDA_LOJA } from './contratoVendaConfig';

export interface ContratoVendaVariables {
  nomeVendedora: string;
  cnpjVendedora: string;
  enderecoVendedora: string;
  bairroVendedora: string;
  cidadeVendedora: string;
  ufVendedora: string;
  cepVendedora: string;
  nomeComprador: string;
  nacionalidade: string;
  rgComprador: string;
  orgaoEmissorRg: string;
  cpfComprador: string;
  enderecoComprador: string;
  cepComprador: string;
  telefoneComprador: string;
  modeloVeiculo: string;
  anoModelo: string;
  chassi: string;
  corVeiculo: string;
  placa: string;
  km: string;
  valorTotal: string;
  valorTotalExtenso: string;
  textoParcelas: string;
  banco: string;
  agencia: string;
  contaCorrente: string;
  titularConta: string;
  chavePix: string;
  enderecoEntrega: string;
  cidadeEntrega: string;
  ufEntrega: string;
  cepEntrega: string;
  cidadeForo: string;
  ufForo: string;
  cidadeAssinatura: string;
  ufAssinatura: string;
  dataExtensoAssinatura: string;
}

const BLANK = '________________________________';

const formatDateExtenso = (dateString?: string) => {
  if (!dateString) return BLANK;
  try {
    const d = new Date(dateString.length > 10 ? dateString : `${dateString}T12:00:00`);
    if (isNaN(d.getTime())) return BLANK;
    return format(d, "dd 'de' MMMM 'de' yyyy", { locale: ptBR });
  } catch {
    return BLANK;
  }
};

const formatDateShort = (dateString?: string | null) => {
  if (!dateString) return BLANK;
  try {
    const raw = String(dateString).slice(0, 10);
    return format(parseISO(`${raw}T12:00:00`), 'dd/MM/yyyy', { locale: ptBR });
  } catch {
    return String(dateString);
  }
};

export const formatCurrency = (value: number) =>
  value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

export const formatCpf = (cpf?: string) => {
  const digits = (cpf || '').replace(/\D/g, '');
  if (digits.length !== 11) return cpf || BLANK;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
};

const parseInstallmentAmounts = (sale: Sale): number[] => {
  if (sale.installments?.length) {
    return sale.installments.map((i) => Number(i.amount)).filter((n) => Number.isFinite(n));
  }
  const raw = sale.installment_amounts;
  if (Array.isArray(raw)) return raw.map((n) => Number(n)).filter((n) => Number.isFinite(n));
  if (typeof raw === 'string' && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed.map((n) => Number(n)).filter((n) => Number.isFinite(n));
    } catch {
      /* ignore */
    }
  }
  const count = Math.max(0, Number(sale.installment_count) || 0);
  const single = Number(sale.installment_amount);
  if (count > 0 && Number.isFinite(single)) return Array(count).fill(single);
  return [];
};

const buildTextoParcelas = (sale: Sale): string => {
  const valorTotal = formatCurrency(Number(sale.sale_price) || 0);
  const metodo = sale.payment_method ? PAYMENT_METHODS[sale.payment_method] : BLANK;

  if (sale.payment_method === 'term') {
    const entrada = Number(sale.down_payment) || 0;
    const parcelas = parseInstallmentAmounts(sale);
    const linhas: string[] = [];

    if (entrada > 0) {
      linhas.push(`Entrada de ${formatCurrency(entrada)}, na data da assinatura deste contrato ou conforme acordado entre as partes.`);
    }

    if (sale.installments?.length) {
      sale.installments.forEach((p: SaleInstallment, idx) => {
        linhas.push(
          `${idx + 1}ª parcela de ${formatCurrency(Number(p.amount))}, com vencimento em ${formatDateShort(p.due_date)}.`
        );
      });
    } else if (parcelas.length > 0) {
      const baseDate = sale.next_installment_due_date || sale.sale_date;
      parcelas.forEach((valor, idx) => {
        linhas.push(
          `${idx + 1}ª parcela de ${formatCurrency(valor)}${
            idx === 0 && baseDate ? `, com vencimento em ${formatDateShort(baseDate)}` : ''
          }.`
        );
      });
    } else if (sale.installment_count) {
      linhas.push(
        `${sale.installment_count} parcela(s) conforme valores e vencimentos acordados entre as partes, totalizando ${valorTotal}.`
      );
    }

    if (linhas.length === 0) {
      linhas.push(`Pagamento a prazo, totalizando ${valorTotal}, conforme condições acordadas entre as partes.`);
    }

    return linhas.join('\n');
  }

  return `Pagamento à vista mediante ${metodo}, no valor total de ${valorTotal}, na data de ${formatDateShort(sale.sale_date)} ou conforme acordado entre as partes.`;
};

const unidades = ['', 'um', 'dois', 'três', 'quatro', 'cinco', 'seis', 'sete', 'oito', 'nove'];
const especiais = [
  'dez',
  'onze',
  'doze',
  'treze',
  'quatorze',
  'quinze',
  'dezesseis',
  'dezessete',
  'dezoito',
  'dezenove',
];
const dezenas = ['', '', 'vinte', 'trinta', 'quarenta', 'cinquenta', 'sessenta', 'setenta', 'oitenta', 'noventa'];
const centenas = [
  '',
  'cento',
  'duzentos',
  'trezentos',
  'quatrocentos',
  'quinhentos',
  'seiscentos',
  'setecentos',
  'oitocentos',
  'novecentos',
];

const numeroAte999 = (n: number): string => {
  if (n === 0) return '';
  if (n === 100) return 'cem';
  if (n < 10) return unidades[n];
  if (n < 20) return especiais[n - 10];
  if (n < 100) {
    const d = Math.floor(n / 10);
    const u = n % 10;
    return u ? `${dezenas[d]} e ${unidades[u]}` : dezenas[d];
  }
  const c = Math.floor(n / 100);
  const resto = n % 100;
  const base = n === 100 ? 'cem' : centenas[c];
  return resto ? `${base} e ${numeroAte999(resto)}` : base;
};

const numeroAte999999 = (n: number): string => {
  if (n < 1000) return numeroAte999(n);
  if (n < 1000000) {
    const mil = Math.floor(n / 1000);
    const resto = n % 1000;
    const milTxt = mil === 1 ? 'mil' : `${numeroAte999(mil)} mil`;
    return resto ? `${milTxt} e ${numeroAte999(resto)}` : milTxt;
  }
  const milhao = Math.floor(n / 1000000);
  const resto = n % 1000000;
  const milhaoTxt = milhao === 1 ? 'um milhão' : `${numeroAte999(milhao)} milhões`;
  return resto ? `${milhaoTxt} e ${numeroAte999999(resto)}` : milhaoTxt;
};

/** Converte valor monetário para extenso simplificado (reais). */
export const valorPorExtenso = (valor: number): string => {
  const inteiro = Math.floor(Math.abs(valor));
  const centavos = Math.round((Math.abs(valor) - inteiro) * 100);

  if (inteiro === 0 && centavos === 0) return 'zero reais';

  const reais =
    inteiro === 0
      ? ''
      : inteiro === 1
        ? 'um real'
        : `${numeroAte999999(inteiro)} reais`;

  const cents =
    centavos > 0
      ? centavos === 1
        ? 'um centavo'
        : `${numeroAte999(centavos)} centavos`
      : '';

  if (reais && cents) return `${reais} e ${cents}`;
  return reais || cents;
};

export const buildContratoVendaVariables = (
  vehicle: Vehicle,
  sale: Sale,
  dataEmissao?: string
): ContratoVendaVariables => {
  const loja = CONTRATO_VENDA_LOJA;
  const preco = Number(sale.sale_price) || 0;
  const dataRef = dataEmissao || sale.sale_date || new Date().toISOString().split('T')[0];

  const enderecoComprador = [
    sale.buyer_address,
    sale.buyer_city && sale.buyer_state ? `${sale.buyer_city}/${sale.buyer_state}` : sale.buyer_city,
  ]
    .filter(Boolean)
    .join(', ');

  return {
    nomeVendedora: loja.nome,
    cnpjVendedora: loja.cnpj,
    enderecoVendedora: loja.endereco,
    bairroVendedora: loja.bairro,
    cidadeVendedora: loja.cidade,
    ufVendedora: loja.uf,
    cepVendedora: loja.cep,
    nomeComprador: sale.buyer_name || sale.customer_name || BLANK,
    nacionalidade: 'brasileiro(a)',
    rgComprador: BLANK,
    orgaoEmissorRg: BLANK,
    cpfComprador: formatCpf(sale.buyer_document),
    enderecoComprador: enderecoComprador || BLANK,
    cepComprador: sale.buyer_zip || BLANK,
    telefoneComprador: sale.buyer_phone || sale.customer_contact || BLANK,
    modeloVeiculo: `${vehicle.brand} ${vehicle.model}`.trim(),
    anoModelo: String(vehicle.year),
    chassi: vehicle.chassis || BLANK,
    corVeiculo: vehicle.color || BLANK,
    placa: vehicle.plate || vehicle.identification || BLANK,
    km: vehicle.km || vehicle.hours || BLANK,
    valorTotal: formatCurrency(preco),
    valorTotalExtenso: valorPorExtenso(preco),
    textoParcelas: buildTextoParcelas(sale),
    banco: loja.banco,
    agencia: loja.agencia,
    contaCorrente: loja.contaCorrente,
    titularConta: loja.titularConta,
    chavePix: loja.chavePix,
    enderecoEntrega: loja.enderecoEntrega,
    cidadeEntrega: loja.cidadeEntrega,
    ufEntrega: loja.ufEntrega,
    cepEntrega: loja.cepEntrega,
    cidadeForo: loja.cidadeForo,
    ufForo: loja.ufForo,
    cidadeAssinatura: loja.cidade,
    ufAssinatura: loja.uf,
    dataExtensoAssinatura: formatDateExtenso(dataRef),
  };
};

export type ContratoParagraphKind = 'title' | 'heading' | 'body' | 'blank' | 'signature';

export interface ContratoParagraph {
  kind: ContratoParagraphKind;
  text: string;
}

export const buildContratoVendaParagraphs = (v: ContratoVendaVariables): ContratoParagraph[] => {
  const p = (text: string, kind: ContratoParagraphKind = 'body'): ContratoParagraph => ({ kind, text });

  return [
    p('CONTRATO PARTICULAR DE COMPRA E VENDA DE VEÍCULO SEMINOVO', 'title'),
    p('PARTES CONTRATANTES', 'heading'),
    p(
      `VENDEDORA: ${v.nomeVendedora}, pessoa jurídica de direito privado, inscrita no CNPJ sob o nº ${v.cnpjVendedora}, com sede na ${v.enderecoVendedora}, Bairro ${v.bairroVendedora}, ${v.cidadeVendedora}/${v.ufVendedora}, CEP ${v.cepVendedora}, neste ato representada na forma de seu contrato social, doravante denominada simplesmente VENDEDORA.`
    ),
    p(
      `COMPRADOR(A): ${v.nomeComprador} (${v.nacionalidade}), portador(a) do RG nº ${v.rgComprador}, expedido por ${v.orgaoEmissorRg}, inscrito(a) no CPF sob o nº ${v.cpfComprador}, residente e domiciliado(a) em ${v.enderecoComprador}, CEP ${v.cepComprador}, telefone para contato ${v.telefoneComprador}, doravante denominado(a) simplesmente COMPRADOR(A).`
    ),
    p(
      'As partes acima qualificadas celebram o presente Contrato Particular de Compra e Venda de Veículo Seminovo, que se regerá pelas cláusulas e condições a seguir estipuladas, em conformidade com o Código Civil (Lei nº 10.406/2002) e, no que couber, com o Código de Defesa do Consumidor (Lei nº 8.078/1990), que voluntariamente aceitam e outorgam:'
    ),
    p('CLÁUSULA PRIMEIRA — DO OBJETO', 'heading'),
    p('1.1. Por meio do presente instrumento, a VENDEDORA vende ao(à) COMPRADOR(A) o seguinte veículo seminovo:'),
    p(`Veículo: ${v.modeloVeiculo}`),
    p(`Ano/Modelo: ${v.anoModelo}`),
    p(`Chassi: ${v.chassi}`),
    p(`Cor: ${v.corVeiculo}`),
    ...(v.placa !== '________________________________' ? [p(`Placa/Identificação: ${v.placa}`)] : []),
    ...(v.km !== '________________________________' ? [p(`Quilometragem/Horas: ${v.km}`)] : []),
    p(
      '1.2. A VENDEDORA declara ser legítima possuidora e/ou proprietária do veículo acima identificado, responsabilizando-se pela evicção nos termos dos arts. 447 a 457 do Código Civil.'
    ),
    p(
      '1.3. A VENDEDORA declara que o veículo, na data da celebração deste contrato, encontra-se livre e desembaraçado de quaisquer ônus, gravames, multas, débitos de IPVA, licenciamento, DPVAT/SPVAT, restrições judiciais, administrativas ou de qualquer outra natureza, respondendo por eventuais débitos ou pendências anteriores à data da tradição do bem.'
    ),
    p(
      '1.4. Tratando-se de veículo seminovo, o(a) COMPRADOR(A) declara ter plena ciência de que o bem possui desgaste natural compatível com o tempo de uso e a quilometragem indicada, não constituindo tal desgaste vício oculto ou defeito passível de reclamação.'
    ),
    p('CLÁUSULA SEGUNDA — DA VISTORIA E CIÊNCIA DO ESTADO DO VEÍCULO', 'heading'),
    p(
      '2.1. O(A) COMPRADOR(A) declara, para todos os fins de direito, que, previamente à celebração do presente contrato, realizou vistoria minuciosa no veículo descrito na Cláusula Primeira, por si ou por profissional técnico de sua confiança, tendo verificado suas condições gerais de funcionamento, conservação, aparência, estado mecânico, elétrico, estrutural e de lataria/pintura, declarando-se plenamente satisfeito(a) com o estado do bem.'
    ),
    p(
      '2.2. As partes firmaram, conjuntamente, Check List de Vistoria contendo descrição detalhada das condições do veículo e registro fotográfico, o qual constitui parte integrante e indissociável do presente contrato, independentemente de transcrição.'
    ),
    p(
      '2.3. O(A) COMPRADOR(A) declara ter sido informado(a) pela VENDEDORA acerca de todas as características do veículo, incluindo eventual histórico de sinistro, reparos realizados, substituição de peças e quaisquer outras informações relevantes sobre o estado do bem, nos termos do art. 6º, III, do Código de Defesa do Consumidor.'
    ),
    p(
      '2.4. Fica consignado que o veículo foi entregue ao(à) COMPRADOR(A) com a quilometragem informada acima, quilometragem esta aferida e certificada no ato da entrega, mediante leitura do hodômetro original do veículo.'
    ),
    p('CLÁUSULA TERCEIRA — DA GARANTIA', 'heading'),
    p(
      '3.1. Em conformidade com o disposto no art. 26, II, do Código de Defesa do Consumidor (Lei nº 8.078/1990), o veículo objeto deste contrato possui garantia legal de 90 (noventa) dias, contados da data da efetiva entrega do bem ao(à) COMPRADOR(A), para reclamação de vícios aparentes ou de fácil constatação.'
    ),
    p(
      '3.2. Tratando-se de vícios ocultos, o prazo decadencial de 90 (noventa) dias para reclamação inicia-se a partir da constatação do defeito, nos termos do art. 26, §3º, do CDC.'
    ),
    p('3.3. A garantia prevista nesta cláusula não abrange:'),
    p(
      'a) Peças e componentes sujeitos a desgaste natural pelo uso, tais como: pneus, pastilhas e discos de freio, embreagem, correias, lâmpadas, fusíveis, palhetas de limpador, bateria, filtros, fluidos, óleos e demais itens de manutenção periódica;'
    ),
    p(
      'b) Danos decorrentes de mau uso, negligência, imperícia na condução, acidentes, enchentes, incêndio ou caso fortuito/força maior após a tradição do bem;'
    ),
    p(
      'c) Danos decorrentes de reparos, manutenções ou modificações realizadas por terceiros não autorizados pela VENDEDORA ou em oficinas não credenciadas;'
    ),
    p('d) Itens de personalização, acessórios ou equipamentos não originais de fábrica;'),
    p(
      'e) Problemas causados pela utilização de combustíveis, lubrificantes ou fluidos incompatíveis com as especificações do fabricante.'
    ),
    p(
      '3.4. Para exercer o direito de garantia, o(a) COMPRADOR(A) deverá comunicar a VENDEDORA por escrito, no prazo legal, descrevendo o vício constatado, e encaminhar o veículo às dependências da VENDEDORA ou oficina por esta indicada para avaliação técnica, a qual deverá ser realizada no prazo de até 30 (trinta) dias, nos termos do art. 18, §1º, do CDC.'
    ),
    p(
      '3.5. Comprovado que o vício reclamado se enquadra nas hipóteses de exclusão previstas na cláusula 3.3, ou que decorre de desgaste natural pelo uso, a VENDEDORA ficará isenta de responsabilidade pela reparação.'
    ),
    p('CLÁUSULA QUARTA — DO PREÇO E CONDIÇÕES DE PAGAMENTO', 'heading'),
    p(
      `4.1. O preço certo e ajustado para a venda do veículo é de ${v.valorTotal} (${v.valorTotalExtenso}) a ser pago pelo(a) COMPRADOR(A) à VENDEDORA da seguinte forma:`
    ),
    p(v.textoParcelas),
    p(
      '4.2. O pagamento deverá ser realizado exclusivamente mediante depósito bancário ou transferência eletrônica (PIX/TED/DOC) para a seguinte conta:'
    ),
    p(
      `Banco: ${v.banco} | Agência: ${v.agencia} | Conta Corrente: ${v.contaCorrente}\nCNPJ: ${v.cnpjVendedora} | Titular: ${v.titularConta}\nChave PIX: ${v.chavePix}`
    ),
    p(
      '4.3. Os comprovantes de pagamento deverão ser apresentados à VENDEDORA no ato da transferência ou sempre que solicitado, servindo como meio de prova da quitação.'
    ),
    p(
      '4.4. O não pagamento de qualquer parcela no vencimento importará na constituição automática do(a) COMPRADOR(A) em mora, independentemente de notificação ou interpelação judicial ou extrajudicial, nos termos do art. 397 do Código Civil, incidindo:\na) Multa moratória de 2% (dois por cento) sobre o valor da parcela em atraso;\nb) Juros de mora de 1% (um por cento) ao mês, calculados pro rata die;\nc) Correção monetária pelo INPC/IBGE ou índice que o substitua, a partir do vencimento.'
    ),
    p(
      '4.5. Em caso de inadimplemento, a VENDEDORA poderá adotar as medidas judiciais e extrajudiciais cabíveis para a satisfação do crédito, ficando o(a) COMPRADOR(A) responsável pelo pagamento de honorários advocatícios de 20% (vinte por cento) sobre o valor do débito atualizado, além das custas processuais.'
    ),
    p(
      '4.6. O atraso superior a 30 (trinta) dias no pagamento de qualquer parcela facultará à VENDEDORA a resolução do contrato, com a retomada do veículo, deduzindo-se dos valores já pagos pelo(a) COMPRADOR(A) a quantia correspondente a eventual depreciação do bem e despesas suportadas pela VENDEDORA, restituindo-se o saldo remanescente, se houver.'
    ),
    p('CLÁUSULA QUINTA — DA TRADIÇÃO E LOCAL DE ENTREGA', 'heading'),
    p(
      `5.1. O veículo será entregue ao(à) COMPRADOR(A) exclusivamente nas dependências da ${v.nomeVendedora}, localizada na ${v.enderecoEntrega}, ${v.cidadeEntrega}/${v.ufEntrega}, CEP ${v.cepEntrega}.`
    ),
    p(
      '5.2. A tradição do veículo opera a transferência dos riscos ao(à) COMPRADOR(A), nos termos do art. 492 do Código Civil, sendo este(a) responsável, a partir de então, por quaisquer danos, perdas, furto, roubo, sinistro ou avarias que venham a ocorrer.'
    ),
    p(
      '5.3. Caso o(a) COMPRADOR(A) deseje que o veículo seja transportado ou entregue em endereço diverso, todos os custos, riscos e responsabilidades decorrentes do transporte serão de sua exclusiva responsabilidade, isentando a VENDEDORA de qualquer obrigação ou ônus.'
    ),
    p(
      '5.4. No ato da retirada do veículo, as partes firmarão Termo de Entrega e Recebimento, registrando a data, hora, quilometragem e condições do veículo, o qual passará a integrar o presente contrato.'
    ),
    p('CLÁUSULA SEXTA — DA TRANSFERÊNCIA DE PROPRIEDADE', 'heading'),
    p(
      '6.1. O(A) COMPRADOR(A) obriga-se a providenciar a transferência de propriedade do veículo junto ao órgão de trânsito competente (DETRAN) no prazo máximo de 30 (trinta) dias contados da assinatura deste instrumento, às suas expensas, providenciando toda a documentação necessária nos termos do art. 123 do Código de Trânsito Brasileiro (Lei nº 9.503/1997).'
    ),
    p(
      '6.2. O descumprimento do prazo previsto na cláusula anterior sujeitará o(a) COMPRADOR(A) ao pagamento de multa de 5% (cinco por cento) sobre o valor total do contrato, sem prejuízo da responsabilidade integral por quaisquer multas de trânsito, tributos, encargos ou débitos incidentes sobre o veículo até a efetiva transferência.'
    ),
    p(
      '6.3. Caso o(a) COMPRADOR(A) não disponha de despachante para a realização da transferência, a VENDEDORA poderá prestar tal serviço, mediante cobrança de valor previamente informado e aceito pelo(a) COMPRADOR(A).'
    ),
    p(
      '6.4. O(A) COMPRADOR(A) responderá, a partir da tradição do bem, por todas as infrações de trânsito cometidas com o veículo, obrigando-se a indicar-se como condutor infrator perante o órgão competente, isentando a VENDEDORA de qualquer responsabilidade.'
    ),
    p('CLÁUSULA SÉTIMA — DAS DESPESAS', 'heading'),
    p(
      '7.1. Todas as despesas com a transferência do veículo, incluindo taxas do DETRAN, emolumentos, vistoria e serviços de despachante, serão de responsabilidade exclusiva do(a) COMPRADOR(A).'
    ),
    p(
      '7.2. O pagamento de IPVA, licenciamento e seguro obrigatório (DPVAT/SPVAT) referente ao exercício corrente e anteriores à data de celebração deste contrato são de responsabilidade da VENDEDORA. A partir da data da celebração, tais encargos passam a ser de responsabilidade do(a) COMPRADOR(A), proporcionalmente quando cabível.'
    ),
    p('CLÁUSULA OITAVA — DA VEDAÇÃO À CESSÃO', 'heading'),
    p(
      '8.1. É vedada a cessão ou transferência dos direitos e obrigações decorrentes deste contrato, a qualquer título, sem a prévia e expressa anuência da outra parte, formalizada por escrito.'
    ),
    p('CLÁUSULA NONA — DA CLÁUSULA PENAL E RESCISÃO', 'heading'),
    p(
      '9.1. O descumprimento de qualquer obrigação prevista neste contrato, ressalvadas as hipóteses com penalidades específicas, sujeitará a parte infratora ao pagamento de multa compensatória de 10% (dez por cento) sobre o valor total do contrato, sem prejuízo das perdas e danos efetivamente comprovados.'
    ),
    p(
      '9.2. A rescisão do contrato por culpa exclusiva do(a) COMPRADOR(A) importará na retenção, pela VENDEDORA, de 20% (vinte por cento) do valor total do contrato, a título de cláusula penal compensatória e ressarcimento de despesas administrativas, restituindo-se o saldo eventualmente devido.'
    ),
    p('CLÁUSULA DÉCIMA — DAS DECLARAÇÕES DO(A) COMPRADOR(A)', 'heading'),
    p('10.1. O(A) COMPRADOR(A) declara, para todos os fins de direito, que:'),
    p(
      'a) Leu integralmente e compreendeu todas as cláusulas e condições do presente contrato, tendo tido a oportunidade de esclarecer dúvidas antes da assinatura;'
    ),
    p(
      'b) Realizou vistoria no veículo objeto deste contrato, por si ou por técnico de sua confiança, estando ciente de suas condições gerais, conforme Check List de Vistoria anexo;'
    ),
    p(
      'c) Tem plena ciência de que se trata de veículo seminovo, com desgaste natural compatível com o tempo de uso e quilometragem, não se caracterizando tal condição como vício ou defeito;'
    ),
    p(
      'd) A garantia legal aplicável ao presente negócio é de 90 (noventa) dias para vícios aparentes, nos termos do art. 26, II, do CDC, não abrangendo peças de desgaste natural e itens consumíveis;'
    ),
    p('e) Está ciente da obrigatoriedade de proceder à transferência de propriedade do veículo no prazo estipulado na Cláusula Sexta;'),
    p(
      'f) Assume integral responsabilidade pelo veículo a partir da efetiva tradição, incluindo riscos de perecimento, avarias, multas de trânsito e danos a terceiros.'
    ),
    p('CLÁUSULA DÉCIMA PRIMEIRA — DA SUCESSÃO', 'heading'),
    p(
      '11.1. Em caso de falecimento ou extinção de qualquer das partes, as obrigações e direitos decorrentes deste contrato transmitir-se-ão aos respectivos herdeiros e sucessores, na forma da lei.'
    ),
    p('CLÁUSULA DÉCIMA SEGUNDA — DAS ALTERAÇÕES', 'heading'),
    p(
      '12.1. Qualquer alteração ao presente contrato somente terá validade se formalizada mediante Termo Aditivo escrito, assinado por ambas as partes.'
    ),
    p('CLÁUSULA DÉCIMA TERCEIRA — DA VIGÊNCIA', 'heading'),
    p(
      '13.1. O presente contrato entra em vigor na data de sua assinatura e produzirá efeitos até o integral cumprimento de todas as obrigações nele previstas.'
    ),
    p('CLÁUSULA DÉCIMA QUARTA — DO TÍTULO EXECUTIVO EXTRAJUDICIAL', 'heading'),
    p(
      '14.1. As partes reconhecem e declaram que o presente instrumento, firmado por duas testemunhas, constitui título executivo extrajudicial, nos termos do art. 784, III, do Código de Processo Civil (Lei nº 13.105/2015), podendo ser executado judicialmente em caso de inadimplemento de qualquer obrigação nele prevista.'
    ),
    p('CLÁUSULA DÉCIMA QUINTA — DA PROTEÇÃO DE DADOS PESSOAIS', 'heading'),
    p(
      '15.1. As partes comprometem-se a tratar os dados pessoais obtidos por meio deste contrato em conformidade com a Lei Geral de Proteção de Dados Pessoais (Lei nº 13.709/2018 — LGPD), utilizando-os exclusivamente para as finalidades decorrentes da relação contratual aqui estabelecida.'
    ),
    p('CLÁUSULA DÉCIMA SEXTA — DO FORO', 'heading'),
    p(
      `16.1. As partes elegem o foro da Comarca de ${v.cidadeForo}/${v.ufForo} para dirimir quaisquer controvérsias oriundas do presente contrato, com renúncia expressa a qualquer outro, por mais privilegiado que seja.`
    ),
    p(
      'E, por estarem justas e contratadas, as partes firmam o presente instrumento em 02 (duas) vias de igual teor e forma, na presença de 02 (duas) testemunhas abaixo nomeadas, para que produza seus regulares efeitos jurídicos.'
    ),
    p(`${v.cidadeAssinatura}/${v.ufAssinatura}, ${v.dataExtensoAssinatura}.`, 'signature'),
    p(`_______________________________________________\nVENDEDORA — ${v.nomeVendedora}`, 'signature'),
    p('_______________________________________________\nCOMPRADOR(A)', 'signature'),
    p('TESTEMUNHAS:', 'heading'),
    p('_______________________________________________\n1ª Testemunha — Nome: _______________ CPF: _______________', 'signature'),
    p('_______________________________________________\n2ª Testemunha — Nome: _______________ CPF: _______________', 'signature'),
  ];
};

/** Divide parágrafos em páginas para o PDF (evita corte no fim da folha). */
export const chunkContratoParagraphsForPdf = (
  paragraphs: ContratoParagraph[],
  perPage = 28
): ContratoParagraph[][] => {
  const chunks: ContratoParagraph[][] = [];
  for (let i = 0; i < paragraphs.length; i += perPage) {
    chunks.push(paragraphs.slice(i, i + perPage));
  }
  return chunks.length ? chunks : [[]];
};
